
import EstadoTag from "@/components/widgets/EstadoTag.vue";
import localidades from "@/core/data/localidades";
import provincias from "@/core/data/provincias";
import { sectores, sectoresProfesionales } from "@/core/data/sectores";
import tratamientos from "@/core/data/tratamientos";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { InscripcionEstado } from "@/enums";
import Page from "@/layout/Page.vue";
import { CursosModule } from "@/store/modules";
import moment from "moment";
import "moment/locale/es";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { ErrorMessage, useField, useForm } from "vee-validate";
import { computed, defineComponent, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { getModule } from "vuex-module-decorators";
import * as Yup from "yup";

interface InscripcionEditar {
  estado: string;
  nombre: string;
  apellidos: string;
  dni: string;
  email: string;
  telefono: string;
  localidad: string;
  provincia: string;
  codigoPostal: string;
  presencial?: boolean;
  videoconferencia?: boolean;
  perfil: string;
  nombreEmpresa?: string | null;
  localidadEmpresa?: string | null;
  cifEmpresa?: string | null;
  sectorEmpresa?: string | null;
  empleadosEmpresa?: string | null;
  proyectoEmprendimiento?: string | null;
  puesto?: string | null;
  entidad?: string | null;
  createdOn: Date;
}

interface Localidad {
  label: string;
  value: string;
}
interface Sector {
  label: string;
  value: string;
}

export default defineComponent({
  name: "listados-servicio-detalle",
  components: {
    ErrorMessage,
    Page,
    EstadoTag,
  },

  methods: {
    formatDate(fecha) {
      return moment(fecha).format("DD/MM/YYYY hh:mm");
    },
  },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const token = route.params.id;

    const store = useStore();
    const cursosModule = getModule(CursosModule, store);

    const cargaDetalle = ref(false);
    const crearCurso = ref(false);

    const cursoPresencial = ref(false);
    const cursoVideoconferencia = ref(false);
    const cursoEmprendimiento = ref(false);

    const emprendimientoAllowedValues = ["Digital", "Emprendimiento"];

    onBeforeMount(async () => {
      cargaDetalle.value = true;
      try {
        await cursosModule.fetchCursoInscripcionDetalle({
          id: token as string,
        });
      } catch (err) {
        Swal.fire({
          text: t("formularios.inscripcionCursoDetalle.cargar.error"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("dialogs.okButton"),
          customClass: {
            confirmButton: "btn btn-primary",
          },
        });
      } finally {
        cargaDetalle.value = false;
      }
    });

    const datosInscripcion = computed(() => cursosModule.inscripcion);
    const dialogVisible = ref(false);
    const dialogLoading = ref(false);
    const dialogSelected = ref<string>();

    const error = computed(() => cursosModule.errors);
    watch(error, () => {
      if (!error.value) return;

      Swal.fire({
        text:
          t("errors." + (error.value.error.code ?? error.value.error.name)) ??
          error.value.error.name,
        icon: "error",
        buttonsStyling: false,
        confirmButtonText: t("dialogs.okButton"),
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    });
    const cursoUrl = ref("");
    const cursoNombre = ref("");

    let sectoresTodos = ref<Sector[]>([]);
    sectoresTodos.value = sectores.map((s) => {
      return { label: s.nombre, value: s.id };
    });
    let sectoresProfesionalesTodos = ref<Sector[]>([]);
    sectoresProfesionalesTodos.value = sectoresProfesionales.map((s) => {
      return { label: s.nombre, value: s.id };
    });

    let localidadesFiltradas = ref<Localidad[]>([]);
    let localidadesTodas = ref<Localidad[]>([]);

    watch(datosInscripcion, () => {
      if (!datosInscripcion.value) return;

      cursoUrl.value = datosInscripcion.value.cursoId
        ? "/listados/cursos/" + datosInscripcion.value.cursoId
        : "";
      cursoNombre.value = datosInscripcion.value.curso?.nombre as string;

      cursoPresencial.value = datosInscripcion.value.curso
        ?.presencial as boolean;
      cursoVideoconferencia.value = datosInscripcion.value.curso
        ?.videoconferencia as boolean;

      estado.value = datosInscripcion.value.estado;
      estadoOriginal.value = datosInscripcion.value.estado;

      if (
        emprendimientoAllowedValues.includes(
          datosInscripcion.value.curso?.areaTematica as string
        )
      ) {
        cursoEmprendimiento.value = true;
      }

      resetForm({
        values: {
          estado: datosInscripcion.value.estado,
          nombre: datosInscripcion.value.nombre,
          apellidos: datosInscripcion.value.apellidos,
          dni: datosInscripcion.value.dni,
          email: datosInscripcion.value.email,
          telefono: datosInscripcion.value.telefono,
          localidad: datosInscripcion.value.localidad,
          provincia: datosInscripcion.value.provincia,
          codigoPostal: datosInscripcion.value.codigoPostal,
          presencial: datosInscripcion.value.presencial,
          videoconferencia: datosInscripcion.value.videoconferencia,
          perfil: datosInscripcion.value.perfil,
          nombreEmpresa: datosInscripcion.value.nombreEmpresa,
          localidadEmpresa: datosInscripcion.value.localidadEmpresa,
          cifEmpresa: datosInscripcion.value.cifEmpresa,
          sectorEmpresa: datosInscripcion.value.sectorEmpresa,
          empleadosEmpresa: datosInscripcion.value.empleadosEmpresa,
          entidad: datosInscripcion.value.entidad,
          puesto: datosInscripcion.value.puesto,
          proyectoEmprendimiento: datosInscripcion.value.proyectoEmprendimiento,
          createdOn: datosInscripcion.value.createdOn,
        },
      });

      setCurrentPageBreadcrumbs(
        datosInscripcion.value.nombre + " " + datosInscripcion.value.apellidos,
        [
          {
            title: t("menu.cursos"),
            path: "/listados/cursos",
          },
          {
            title: t("menu.inscripciones"),
            path: cursoUrl.value,
          },
        ]
      );

      localidadesTodas.value = localidades
        .map((l) => {
          return { label: l.localidad, value: l.localidad };
        })
        .sort((a, b) => {
          return a.label.localeCompare(b.label);
        });

      localidadesFiltradas.value = localidades
        .filter(
          (loc) =>
            loc.provincia === (datosInscripcion.value?.provincia as string)
        )
        .map((l) => {
          return { label: l.localidad, value: l.localidad };
        })
        .sort((a, b) => {
          if (a.label > b.label) {
            return 1;
          }
          if (a.label < b.label) {
            return -1;
          }
          return 0;
        });
    });

    const updateLocalidades = () => {
      localidad.value = "";
      return (localidadesFiltradas.value = localidades
        .filter((loc) => loc.provincia === provincia.value)
        .map((l) => {
          return { label: l.localidad, value: l.localidad };
        })
        .sort((a, b) => {
          if (a.label > b.label) {
            return 1;
          }
          if (a.label < b.label) {
            return -1;
          }
          return 0;
        }));
    };

    const isValidNIForNIE = (dni) => {
      const nifRegex = /^[0-9]{8}[A-Z]$/;
      const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
      const validChars = "TRWAGMYFPDXBNJZSQVHLCKET";

      if (nifRegex.test(dni)) {
        const number = parseInt(dni.substring(0, 8), 10);
        const letter = validChars.charAt(number % 23);
        return letter === dni.charAt(8);
      }

      if (nieRegex.test(dni)) {
        // Cambiar la X, Y o Z a su equivalente numérico
        let number = dni.substring(1);
        switch (dni.charAt(0)) {
          case "X":
            number = "0" + number;
            break;
          case "Y":
            number = "1" + number;
            break;
          case "Z":
            number = "2" + number;
            break;
        }
        number = parseInt(number, 10);
        const letter = validChars.charAt(number % 23);
        return letter === dni.charAt(8);
      }
      return false;
    };

    // La validación adicional de teléfono y código postal se añade pero se deja comentada. Como hasta ahora en las inscripciones no había un formato válido, si se desea editar una inscripción con estos datos en formato incorrecto no dejaría guardar.
    const schema = Yup.object({
      estado: Yup.string().required(),
      nombre: Yup.string().required("Nombre es obligatorio"),
      apellidos: Yup.string().required("Apellidos es obligatorio"),
      dni: Yup.string()
        .required("DNI es obligatorio")
        .test("is-valid-dni", "Documento no válido", (value) =>
          isValidNIForNIE(value)
        ),
      email: Yup.string()
        .email("Debe ser un correo electrónico válido")
        .required("Email es obligatorio"),
      telefono: Yup.string()
        // .matches(/^[+0-9\s()-.]{9,15}$/, "Debe ser un teléfono válido")
        .required("Teléfono es obligatorio"),
      localidad: Yup.string().required("Localidad es obligatorio"),
      provincia: Yup.string().required("Provincia es obligatorio"),
      codigoPostal: Yup.string()
        .typeError("El código postal debe ser un número")
        // .matches(
        //   /^(?:0[1-9]|[1-4]\d|5[0-2])\d{3}$/,
        //   "Debe ser un código postal válido (5 dígitos)"
        // )
        .required("Código postal es obligatorio"),
      presencial: Yup.boolean(),
      videoconferencia: Yup.boolean(),
      perfil: Yup.string().required(),
      nombreEmpresa: Yup.string().nullable(),
      localidadEmpresa: Yup.string().nullable(),
      cifEmpresa: Yup.string().nullable(),
      sectorEmpresa: Yup.string().nullable(),
      empleadosEmpresa: Yup.string().nullable(),
      entidad: Yup.string().nullable(),
      puesto: Yup.string().nullable(),
      proyectoEmprendimiento: Yup.string().nullable(),
      createdOn: Yup.date().required(),
    });

    const { resetForm, handleSubmit, errors } = useForm<InscripcionEditar>({
      validationSchema: schema,
    });

    const estado = ref<string>("");
    const estadoOriginal = ref<string>("");
    const { value: createdOn } = useField("createdOn");
    const { value: nombre } = useField("nombre");
    const { value: apellidos } = useField("apellidos");
    const { value: dni } = useField("dni");
    const { value: telefono } = useField("telefono");
    const { value: email } = useField("email");
    const { value: localidad } = useField("localidad");
    const { value: provincia } = useField("provincia");
    const { value: codigoPostal } = useField("codigoPostal");
    const { value: presencial } = useField("presencial");
    const { value: videoconferencia } = useField("videoconferencia");
    const { value: perfil } = useField("perfil");
    const { value: nombreEmpresa } = useField("nombreEmpresa");
    const { value: localidadEmpresa } = useField("localidadEmpresa");
    const { value: cifEmpresa } = useField("cifEmpresa");
    const { value: sectorEmpresa } = useField("sectorEmpresa");
    const { value: empleadosEmpresa } = useField("empleadosEmpresa");
    const { value: entidad } = useField("entidad");
    const { value: puesto } = useField("puesto");
    const { value: proyectoEmprendimiento } = useField(
      "proyectoEmprendimiento"
    );

    const editable = computed(() => {
      return datosInscripcion.value ? true : false;
    });

    const inscripcionId = computed(() => datosInscripcion.value?.id);
    const optionsPerfil = [
      {
        value: "EMPRESA",
        label: t("perfiles.EMPRESA"),
      },
      {
        value: "PROFESIONAL",
        label: t("perfiles.PROFESIONAL"),
      },
      {
        value: "ASOCIACION",
        label: t("perfiles.ASOCIACION"),
      },
      {
        value: "EMPRENDEDORA",
        label: t("perfiles.EMPRENDEDORA"),
      },
      {
        value: "MUJERES_BUSQUEDA_EMPLEO",
        label: t("perfiles.MUJERES_BUSQUEDA_EMPLEO"),
      },
      {
        value: "POBLACION_GENERAL",
        label: t("perfiles.POBLACION_GENERAL"),
      },
    ];

    return {
      openServicioColectivoPicker: async () => {
        dialogVisible.value = true;
        dialogLoading.value = true;
        try {
          await cursosModule.fetchListado();
        } catch (err) {
          Swal.fire({
            text: t("formularios.cursosListado.cargar.error"),
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: t("dialogs.okButton"),
            customClass: {
              confirmButton: "btn btn-primary",
            },
          });
        } finally {
          dialogLoading.value = false;
        }
      },
      guardar: handleSubmit(async (values) => {
        const idInscripcion = token as string;

        if (!idInscripcion) return;

        cargaDetalle.value = true;
        return cursosModule
          .updateInscripcion({
            id: idInscripcion,
            inscripcion: {
              nombre: values.nombre,
              apellidos: values.apellidos,
              dni: values.dni,
              email: values.email,
              telefono: values.telefono,
              localidad: values.localidad,
              provincia: values.provincia,
              codigoPostal: parseInt(values.codigoPostal as unknown as string),
              perfil: values.perfil,
              nombreEmpresa: values.nombreEmpresa ?? "",
              localidadEmpresa: values.localidadEmpresa ?? "",
              cifEmpresa: values.cifEmpresa ?? "",
              sectorEmpresa: values.sectorEmpresa ?? "",
              empleadosEmpresa: values.empleadosEmpresa ?? "",
              puesto: values.puesto ?? "",
              entidad: values.entidad ?? "",
              videoconferencia: values.videoconferencia,
              presencial: values.presencial,
              proyectoEmprendimiento: values.proyectoEmprendimiento ?? "",
              estado: estado.value,
            },
          })
          .then(() => {
            Swal.fire({
              text: t("formularios.inscripcionCursoDetalle.editar.exito"),
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: t("dialogs.okButton"),
              customClass: {
                confirmButton: "btn btn-primary",
              },
            }).then(() => {
              router.replace(cursoUrl.value);
            });
          })
          .catch(() => {
            Swal.fire({
              text: t("formularios.inscripcionCursoDetalle.editar.error"),
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: t("dialogs.okButton"),
              customClass: {
                confirmButton: "btn btn-primary",
              },
            });
          })
          .finally(() => {
            cargaDetalle.value = false;
          });
      }),
      estados: [
        {
          value: InscripcionEstado.inscrito,
          label: t("estados.INSCRITO"),
        },
        {
          value: InscripcionEstado.finalizado,
          label: t("estados.FINALIZADO"),
        },
        {
          value: InscripcionEstado.listaDeEspera,
          label: t("estados.LISTA_DE_ESPERA"),
        },
        {
          value: InscripcionEstado.abandono,
          label: t("estados.ABANDONO"),
        },
        {
          value: InscripcionEstado.cancelado,
          label: t("estados.CANCELADO"),
        },
      ],
      dialogVisible,
      dialogLoading,
      dialogSelected,
      /* dialogData: computed(() =>
        serviciosProgramadosModule.servicios.filter((servicio) => {
          return (
            servicio.inscripcionAbierta &&
            new Date(servicio.servicio.finPrevisto) > new Date()
          );
        })
      ),*/
      editable,
      solicitudFamiliaId: inscripcionId,
      errors,
      cargaDetalle,
      crearCurso,
      tratamientos,
      nombre,
      apellidos,
      estado,
      estadoOriginal,
      InscripcionEstado,
      cursoUrl,
      cursoNombre,
      createdOn,
      dni,
      telefono,
      email,
      localidad,
      provincia,
      codigoPostal,
      presencial,
      videoconferencia,
      perfil,
      nombreEmpresa,
      localidadEmpresa,
      cifEmpresa,
      sectorEmpresa,
      empleadosEmpresa,
      entidad,
      puesto,
      proyectoEmprendimiento,
      cursoEmprendimiento,
      optionsPerfil,
      cursoPresencial,
      cursoVideoconferencia,
      provincias,
      sectoresTodos,
      sectoresProfesionalesTodos,
      localidadesFiltradas,
      localidadesTodas,
      updateLocalidades,
    };
  },
});
